/**
 * Layout Configuration Module
 * Provides customizable layout settings for the game UI
 */

import { matchConfig } from './UIConfigAPI.js';

// Default configuration for portrait mode
const defaultPortraitConfig = {
    // Game screen scale multiplier (null = auto-calculated, 'fill' = fill screen, or set to 1, 2, 3, etc.)
    gameScreenScale: 'fill',

    // Screen scaling mode
    useDiscreteMultipliers: false, // When true, only allows 1x, 2x, 3x, etc. When false, allows free scaling
    freeScaleSize: 1, // Scale factor when useDiscreteMultipliers is false (1.0 = 240x160, 2.0 = 480x320, etc.)

    // Screen offset from top (in pixels)
    screenTopOffset: 1,

    // Screen border settings
    screenBorderEnabled: false,
    screenBorderRadius: 0, // Border radius in pixels (0 = square corners)

    // Image rendering mode
    imageRendering: 'pixelated', // Options: 'auto', 'pixelated', 'crisp-edges', 'smooth'

    // Control group button sizes (in CSS pixels)
    buttonSizes: {
        dpad: 44,
        ab: 44,
        startSelect: 20, // Height for start/select buttons
    },

    // Control group button font sizes (in CSS pixels)
    buttonFontSizes: {
        ab: 20,
        startSelect: 20,
    },

    // Control group offsets (in pixels)
    offsets: {
        dpad: {
            bottom: 64,
            left: 12,
        },
        ab: {
            bottom: 64,
            right: 12,
            gap: 44, // Gap between A and B buttons
            verticalAlignment: 33, // Vertical alignment percentage (50% = horizontal, >50% = B up/A down, <50% = B down/A up)
        },
        startSelect: {
            bottom: 20,
            gap: 44, // Gap between Start and Select buttons
        },
    },
};

// Default configuration for landscape mode
const defaultLandscapeConfig = {
    gameScreenScale: 1,
    useDiscreteMultipliers: true,
    freeScaleSize: 1.0,
    screenTopOffset: 15,
    screenBorderEnabled: false,
    screenBorderRadius: 0,
    imageRendering: 'pixelated',
    buttonSizes: {
        dpad: 44,
        ab: 44,
        startSelect: 20,
    },

    // Control group button font sizes (in CSS pixels)
    buttonFontSizes: {
        ab: 20,
        startSelect: 20,
    },
    offsets: {
        dpad: {
            bottom: 44,
            left: 0,
        },
        ab: {
            bottom: 44,
            right: 0,
            gap: 24, // Gap between A and B buttons
            verticalAlignment: 34, // Vertical alignment percentage (50% = horizontal, >50% = B up/A down, <50% = B down/A up)
        },
        startSelect: {
            bottom: 7,
            gap: 38, // Gap between Start and Select buttons
        },
    },
};

// Current configuration (separate for portrait and landscape)
let config = {
    portrait: JSON.parse(JSON.stringify(defaultPortraitConfig)),
    landscape: JSON.parse(JSON.stringify(defaultLandscapeConfig)),
};

// Session-only: true when we started with no localStorage and are using API defaults
let sessionUsingApiDefaults = false;
// Session-only: have we loaded API default for each orientation this session?
let apiDefaultLoadedPortrait = false;
let apiDefaultLoadedLandscape = false;

// Load from localStorage if available
// Settings are automatically persisted to localStorage and loaded on page load
const STORAGE_KEY = 'layout_config';

function loadFromStorage() {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
            const parsed = JSON.parse(stored);
            // Validate that stored config has the expected structure
            if (parsed.portrait && parsed.landscape) {
                // Merge with defaults to ensure all properties exist
                // Deep merge for nested objects like buttonSizes, buttonFontSizes, and offsets
                config = {
                    portrait: {
                        ...defaultPortraitConfig,
                        ...parsed.portrait,
                        buttonSizes: {
                            ...defaultPortraitConfig.buttonSizes,
                            ...(parsed.portrait.buttonSizes || {}),
                        },
                        buttonFontSizes: {
                            ...defaultPortraitConfig.buttonFontSizes,
                            ...(parsed.portrait.buttonFontSizes || {}),
                        },
                        offsets: {
                            ...defaultPortraitConfig.offsets,
                            ...(parsed.portrait.offsets || {}),
                            dpad: {
                                ...defaultPortraitConfig.offsets.dpad,
                                ...(parsed.portrait.offsets?.dpad || {}),
                            },
                            ab: {
                                ...defaultPortraitConfig.offsets.ab,
                                ...(parsed.portrait.offsets?.ab || {}),
                            },
                            startSelect: {
                                ...defaultPortraitConfig.offsets.startSelect,
                                ...(parsed.portrait.offsets?.startSelect || {}),
                            },
                        },
                    },
                    landscape: {
                        ...defaultLandscapeConfig,
                        ...parsed.landscape,
                        buttonSizes: {
                            ...defaultLandscapeConfig.buttonSizes,
                            ...(parsed.landscape.buttonSizes || {}),
                        },
                        buttonFontSizes: {
                            ...defaultLandscapeConfig.buttonFontSizes,
                            ...(parsed.landscape.buttonFontSizes || {}),
                        },
                        offsets: {
                            ...defaultLandscapeConfig.offsets,
                            ...(parsed.landscape.offsets || {}),
                            dpad: {
                                ...defaultLandscapeConfig.offsets.dpad,
                                ...(parsed.landscape.offsets?.dpad || {}),
                            },
                            ab: {
                                ...defaultLandscapeConfig.offsets.ab,
                                ...(parsed.landscape.offsets?.ab || {}),
                            },
                            startSelect: {
                                ...defaultLandscapeConfig.offsets.startSelect,
                                ...(parsed.landscape.offsets?.startSelect || {}),
                            },
                        },
                    },
                };
            } else {
                // Invalid structure - use defaults
                config = {
                    portrait: JSON.parse(JSON.stringify(defaultPortraitConfig)),
                    landscape: JSON.parse(JSON.stringify(defaultLandscapeConfig)),
                };
            }
        } else {
            // No stored config - use defaults (which are now the new optimized defaults)
            config = {
                portrait: JSON.parse(JSON.stringify(defaultPortraitConfig)),
                landscape: JSON.parse(JSON.stringify(defaultLandscapeConfig)),
            };
        }
    } catch (e) {
        console.warn('Failed to load layout config from storage:', e);
        // If loading fails, use defaults
        config = {
            portrait: JSON.parse(JSON.stringify(defaultPortraitConfig)),
            landscape: JSON.parse(JSON.stringify(defaultLandscapeConfig)),
        };
    }
}

// Save to localStorage
// Called automatically whenever any config value changes
function saveToStorage() {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
    } catch (e) {
        console.warn('Failed to save layout config to storage:', e);
    }
}

// Load saved settings on module initialization
// This happens automatically when the module is first imported
loadFromStorage();

/**
 * Get current configuration for the current orientation
 * @param {boolean} isPortrait - Whether the current orientation is portrait
 * @returns {Object} Deep copy of current configuration for the orientation
 */
export function getConfig(isPortrait = null) {
    if (isPortrait === null) {
        isPortrait = window.innerHeight > window.innerWidth;
    }
    const orientation = isPortrait ? 'portrait' : 'landscape';
    return JSON.parse(JSON.stringify(config[orientation]));
}

/**
 * Update configuration for a specific orientation
 * @param {boolean} isPortrait - Whether updating portrait config
 * @param {Object} updates - Partial config object to merge
 */
export function updateConfig(isPortrait, updates) {
    const orientation = isPortrait ? 'portrait' : 'landscape';
    config[orientation] = { ...config[orientation], ...updates };
    saveToStorage();
}

/**
 * Set button size for a control group
 * @param {boolean} isPortrait - Whether updating portrait config
 * @param {string} group - Control group name ('dpad', 'ab', or 'startSelect')
 * @param {number} size - Button size in CSS pixels
 */
export function setButtonSize(isPortrait, group, size) {
    if (typeof size !== 'number' || size <= 0) {
        throw new Error('Size must be a positive number');
    }
    const orientation = isPortrait ? 'portrait' : 'landscape';
    if (!config[orientation].buttonSizes[group]) {
        throw new Error(`Invalid control group: ${group}. Must be 'dpad', 'ab', or 'startSelect'`);
    }
    config[orientation].buttonSizes[group] = size;
    saveToStorage();
}

/**
 * Set offset for a control group
 * @param {boolean} isPortrait - Whether updating portrait config
 * @param {string} group - Control group name ('dpad', 'ab', or 'startSelect')
 * @param {Object} offsets - Object with offset properties (bottom, left, right)
 */
export function setOffsets(isPortrait, group, offsets) {
    const orientation = isPortrait ? 'portrait' : 'landscape';
    if (!config[orientation].offsets[group]) {
        throw new Error(`Invalid control group: ${group}. Must be 'dpad', 'ab', or 'startSelect'`);
    }
    config[orientation].offsets[group] = { ...config[orientation].offsets[group], ...offsets };
    saveToStorage();
}

/**
 * Set game screen scale multiplier
 * @param {boolean} isPortrait - Whether updating portrait config
 * @param {number|string|null} scale - Scale multiplier (1, 2, 3, etc.), 'fill', or null for auto-calculated
 */
export function setGameScreenScale(isPortrait, scale) {
    const orientation = isPortrait ? 'portrait' : 'landscape';
    if (scale === 'fill') {
        config[orientation].gameScreenScale = 'fill';
    } else if (scale !== null && (typeof scale !== 'number' || scale < 1 || !Number.isInteger(scale))) {
        throw new Error('Scale multiplier must be a positive integer, "fill", or null');
    } else {
        config[orientation].gameScreenScale = scale;
    }
    saveToStorage();
}

/**
 * Set screen top offset
 * @param {boolean} isPortrait - Whether updating portrait config
 * @param {number} offset - Offset from top in pixels
 */
export function setScreenTopOffset(isPortrait, offset) {
    if (typeof offset !== 'number' || offset < 0) {
        throw new Error('Screen top offset must be a non-negative number');
    }
    const orientation = isPortrait ? 'portrait' : 'landscape';
    config[orientation].screenTopOffset = offset;
    saveToStorage();
}

/**
 * Set whether to use discrete multipliers for screen scaling
 * @param {boolean} isPortrait - Whether updating portrait config
 * @param {boolean} useDiscrete - Whether to use discrete multipliers
 */
export function setUseDiscreteMultipliers(isPortrait, useDiscrete) {
    const orientation = isPortrait ? 'portrait' : 'landscape';
    config[orientation].useDiscreteMultipliers = useDiscrete;
    saveToStorage();
}

/**
 * Set free scale size (when discrete multipliers are disabled)
 * @param {boolean} isPortrait - Whether updating portrait config
 * @param {number} scale - Scale factor (1.0 = 240x160, 2.0 = 480x320, etc.)
 */
export function setFreeScaleSize(isPortrait, scale) {
    if (typeof scale !== 'number' || scale <= 0) {
        throw new Error('Free scale size must be a positive number');
    }
    const orientation = isPortrait ? 'portrait' : 'landscape';
    config[orientation].freeScaleSize = scale;
    saveToStorage();
}

/**
 * Set screen border enabled state
 * @param {boolean} isPortrait - Whether updating portrait config
 * @param {boolean} enabled - Whether border is enabled
 */
export function setScreenBorderEnabled(isPortrait, enabled) {
    const orientation = isPortrait ? 'portrait' : 'landscape';
    config[orientation].screenBorderEnabled = enabled;
    saveToStorage();
}

/**
 * Set screen border radius
 * @param {boolean} isPortrait - Whether updating portrait config
 * @param {number} radius - Border radius in pixels
 */
export function setScreenBorderRadius(isPortrait, radius) {
    if (typeof radius !== 'number' || radius < 0) {
        throw new Error('Screen border radius must be a non-negative number');
    }
    const orientation = isPortrait ? 'portrait' : 'landscape';
    config[orientation].screenBorderRadius = radius;
    saveToStorage();
}

/**
 * Set image rendering mode
 * @param {boolean} isPortrait - Whether updating portrait config
 * @param {string} mode - Image rendering mode ('auto', 'pixelated', 'crisp-edges', 'smooth')
 */
export function setImageRendering(isPortrait, mode) {
    const validModes = ['auto', 'pixelated', 'crisp-edges', 'smooth'];
    if (!validModes.includes(mode)) {
        throw new Error(`Image rendering mode must be one of: ${validModes.join(', ')}`);
    }
    const orientation = isPortrait ? 'portrait' : 'landscape';
    config[orientation].imageRendering = mode;
    saveToStorage();
}

/**
 * Set button font size for a control group
 * @param {boolean} isPortrait - Whether updating portrait config
 * @param {string} group - Control group name ('ab' or 'startSelect')
 * @param {number} fontSize - Font size in CSS pixels
 */
export function setButtonFontSize(isPortrait, group, fontSize) {
    if (typeof fontSize !== 'number' || fontSize <= 0) {
        throw new Error('Font size must be a positive number');
    }
    const orientation = isPortrait ? 'portrait' : 'landscape';
    if (!config[orientation].buttonFontSizes[group]) {
        throw new Error(`Invalid control group for font size: ${group}. Must be 'ab' or 'startSelect'`);
    }
    config[orientation].buttonFontSizes[group] = fontSize;
    saveToStorage();
}

/**
 * Reset configuration to defaults for a specific orientation
 * @param {boolean} isPortrait - Whether resetting portrait config
 */
export function resetConfig(isPortrait) {
    const orientation = isPortrait ? 'portrait' : 'landscape';
    config[orientation] = JSON.parse(
        JSON.stringify(orientation === 'portrait' ? defaultPortraitConfig : defaultLandscapeConfig)
    );
    saveToStorage();
}

/**
 * Reset all configurations to defaults
 */
export function resetAllConfig() {
    config = {
        portrait: JSON.parse(JSON.stringify(defaultPortraitConfig)),
        landscape: JSON.parse(JSON.stringify(defaultLandscapeConfig)),
    };
    saveToStorage();
}

/**
 * Check if this is the first time the user is playing (no config has been saved yet)
 * @returns {boolean} True if this is the first time
 */
export function isFirstTimeUser() {
    const stored = localStorage.getItem(STORAGE_KEY);
    return !stored;
}

/**
 * Mark that the user has seen the config menu (not a first-time user anymore)
 */
export function markConfigMenuSeen() {
    // Just ensure config is saved - if config exists, user is not first-time
    saveToStorage();
}

/**
 * Get current viewport (browser window) dimensions, orientation, and device type.
 * Uses the full browser window (innerWidth/innerHeight), not the canvas size.
 * Device name is kept simple (e.g. "iPhone", "iPad") since screen sizes are well defined.
 * @returns {{ width: number, height: number, orientation: 'portrait'|'landscape', deviceName: string|null }}
 */
export function getScreenInfo() {
    const width = window.innerWidth;
    const height = window.innerHeight;
    const orientation = height > width ? 'portrait' : 'landscape';
    let deviceName = null;
    const ua = navigator.userAgent || '';
    if (/iPhone|iPod/i.test(ua)) deviceName = 'iPhone';
    else if (/iPad/i.test(ua)) deviceName = 'iPad';
    else if (/Android/i.test(ua)) deviceName = 'Android';
    else if (/Mobile/i.test(ua)) deviceName = 'Mobile';
    return { width, height, orientation, deviceName };
}

/**
 * Deep-merge a fetched config into config[orientation] and save.
 * Exported so the layout wizard can apply API-matched config as a starting point.
 * @param {boolean} isPortrait
 * @param {Object} fetched
 */
export function applyFetchedConfig(isPortrait, fetched) {
    const orientation = isPortrait ? 'portrait' : 'landscape';
    const defaultOrientation = isPortrait ? defaultPortraitConfig : defaultLandscapeConfig;
    config[orientation] = {
        ...defaultOrientation,
        ...fetched,
        buttonSizes: {
            ...defaultOrientation.buttonSizes,
            ...(fetched.buttonSizes || {}),
        },
        buttonFontSizes: {
            ...defaultOrientation.buttonFontSizes,
            ...(fetched.buttonFontSizes || {}),
        },
        offsets: {
            ...defaultOrientation.offsets,
            ...(fetched.offsets || {}),
            dpad: {
                ...defaultOrientation.offsets?.dpad,
                ...(fetched.offsets?.dpad || {}),
            },
            ab: {
                ...defaultOrientation.offsets?.ab,
                ...(fetched.offsets?.ab || {}),
            },
            startSelect: {
                ...defaultOrientation.offsets?.startSelect,
                ...(fetched.offsets?.startSelect || {}),
            },
        },
    };
    saveToStorage();
}

/**
 * Fetch the best-matching config from the API for the current screen and apply it for the given orientation.
 * Used by the layout wizard to start from an API default when available.
 * @param {boolean} isPortrait
 * @returns {Promise<boolean>} True if a config was applied
 */
export async function fetchAndApplyMatchedConfig(isPortrait) {
    const { width, height, orientation, deviceName } = getScreenInfo();
    if (orientation !== (isPortrait ? 'portrait' : 'landscape')) return false;
    try {
        const result = await matchConfig(width, height, orientation, deviceName);
        if (result.config && result.config.config) {
            applyFetchedConfig(isPortrait, result.config.config);
            return true;
        }
    } catch (e) {
        console.warn('Failed to fetch matched UI config from API:', e);
    }
    return false;
}

/**
 * Load default config from API for the current orientation only (first-time user).
 * Call after loadFromStorage(). Sets sessionUsingApiDefaults and loads only the active orientation.
 * @returns {Promise<void>}
 */
export async function loadDefaultConfig() {
    if (!isFirstTimeUser()) return;
    sessionUsingApiDefaults = true;
    const { width, height, orientation, deviceName } = getScreenInfo();
    const isPortrait = orientation === 'portrait';
    try {
        const result = await matchConfig(width, height, orientation, deviceName);
        if (result.config && result.config.config) {
            applyFetchedConfig(isPortrait, result.config.config);
            if (isPortrait) apiDefaultLoadedPortrait = true;
            else apiDefaultLoadedLandscape = true;
        }
    } catch (e) {
        console.warn('Failed to load default UI config from API:', e);
    }
}

/**
 * When orientation changes: if we're using API defaults and haven't loaded the new orientation yet,
 * fetch and apply the matching config for it. Call from orientationchange handler.
 * @returns {Promise<void>}
 */
export async function ensureDefaultConfigForCurrentOrientation() {
    if (!sessionUsingApiDefaults) return;
    const isPortrait = window.innerHeight > window.innerWidth;
    if (isPortrait && apiDefaultLoadedPortrait) return;
    if (!isPortrait && apiDefaultLoadedLandscape) return;
    const { width, height, orientation, deviceName } = getScreenInfo();
    try {
        const result = await matchConfig(width, height, orientation, deviceName);
        if (result.config && result.config.config) {
            applyFetchedConfig(isPortrait, result.config.config);
            if (isPortrait) apiDefaultLoadedPortrait = true;
            else apiDefaultLoadedLandscape = true;
        }
    } catch (e) {
        console.warn('Failed to load default UI config from API:', e);
    }
}
